// Calls to the Hub API, and the one escaping function every page uses for any
// text it puts into HTML.

export function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly errors: string[] = [],
  ) {
    super(message);
  }
}

export type TokenSource = () => Promise<string>;

export function createApi(getToken: TokenSource, fetcher: typeof fetch = (i, o) => fetch(i, o)) {
  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await getToken();
    const res = await fetcher(path, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as { error?: string; errors?: string[] };
    if (!res.ok) throw new ApiError(res.status, data.error ?? `Request failed (${res.status})`, data.errors ?? []);
    return data as T;
  }
  return {
    get: <T>(path: string) => call<T>('GET', path),
    post: <T>(path: string, body: unknown = {}) => call<T>('POST', path, body),
    put: <T>(path: string, body: unknown) => call<T>('PUT', path, body),
  };
}

export type Api = ReturnType<typeof createApi>;
